import { getCookie, setCookie } from '@atlassian/browser-storage-controls';

type CircuitBreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

type Result = 'PASS' | 'FAIL';

interface CookieData {
	numberRepresentation: number; // sliding window of 0s and 1s
	windowLength: number;
	counter: number;
}
export interface CookieCircuitBreakerConfig {
	cookieKey: string;
	windowSize?: number;
	threshold?: number;
	keepOpenCount?: number;
	halfOpenForCount?: number;
}
export class CookieCircuitBreaker {
	private threshold: number;

	private keepOpenCount: number;

	private halfOpenForCount: number;

	private cookieKey: string;

	private windowSize: number;

	private windowMask: number;

	private cookieData: CookieData;

	constructor({
		cookieKey,
		windowSize = 15,
		threshold = 0.1,
		keepOpenCount = 100,
		halfOpenForCount = 10,
	}: CookieCircuitBreakerConfig) {
		this.cookieKey = cookieKey;
		this.windowSize = windowSize;
		// eslint-disable-next-line no-bitwise
		this.windowMask = (1 << this.windowSize) - 1;
		this.threshold = threshold;
		this.halfOpenForCount = halfOpenForCount;
		this.keepOpenCount = keepOpenCount;

		const existingCookie = getCookie(`cb_${cookieKey}`);
		if (existingCookie) {
			this.cookieData = this.parseCookieData(existingCookie);
		} else {
			this.cookieData = {
				numberRepresentation: 0,
				counter: 0,
				windowLength: 0,
			};
		}
	}

	private parseCookieData(cookieValue: string): CookieData {
		try {
			const [encodedWindow, stringWindowLength, stringCount] = cookieValue.split('|');

			const parsedNumberRepresentation = Number.parseInt(encodedWindow ?? '', 36);
			const numberRepresentation = !Number.isNaN(parsedNumberRepresentation)
				? parsedNumberRepresentation
				: 0;

			const parsedCounter = Number.parseInt(stringCount ?? '', 36);
			const counter = !Number.isNaN(parsedCounter) ? parsedCounter : 0;

			const parsedWindowLength = Number.parseInt(stringWindowLength ?? '', 36);
			const windowLength = !Number.isNaN(parsedWindowLength) ? parsedWindowLength : 0;

			return {
				numberRepresentation,
				counter,
				windowLength,
			};
		} catch (error) {
			// Return default data if parsing fails
			return {
				numberRepresentation: 0,
				counter: 0,
				windowLength: 0,
			};
		}
	}

	private serializeForCookie(): string {
		const encodedWindow = this.cookieData.numberRepresentation.toString(36);
		const stringCount = this.cookieData.counter.toString(36);
		const windowLength = this.cookieData.windowLength.toString(36);
		return `${encodedWindow}|${windowLength}|${stringCount}`;
	}

	public saveToCookie(): void {
		setCookie(`cb_${this.cookieKey}`, this.serializeForCookie(), {
			expires: 14,
			secure: true,
			sameSite: 'strict',
		});
	}

	private addResultToWindow(result: Result): void {
		// eslint-disable-next-line no-bitwise
		this.cookieData.numberRepresentation <<= 1; // Shift left by 1
		// Add 1 for PASS, 0 for FAIL
		// eslint-disable-next-line no-bitwise
		this.cookieData.numberRepresentation |= result === 'PASS' ? 1 : 0;
		// truncate to the window size using bitwise AND
		// eslint-disable-next-line no-bitwise
		this.cookieData.numberRepresentation &= this.windowMask;
		if (this.cookieData.windowLength < this.windowSize) {
			this.cookieData.windowLength++;
		}
	}

	private incrementOrResetOpenCounter() {
		if (this.cookieData.counter >= this.keepOpenCount + this.halfOpenForCount) {
			this.cookieData.counter = 0;
		} else {
			this.cookieData.counter++;
		}
	}

	public recordFailure() {
		return this.recordResult('FAIL');
	}

	public recordPass() {
		return this.recordResult('PASS');
	}

	public recordResult(result: Result): {
		state: CircuitBreakerState;
		pass: number;
		fail: number;
		ratio: number;
	} {
		const state = this.getState();
		switch (state.state) {
			case 'CLOSED':
				this.addResultToWindow(result);
				// reset the counter when circuit is closed
				this.cookieData.counter = 0;
				break;
			case 'OPEN':
				this.cookieData.counter++;
				break;
			case 'HALF_OPEN':
				this.incrementOrResetOpenCounter();
				this.addResultToWindow(result);
				break;
			default:
				throw new Error(`Unknown state: ${state}`);
		}
		this.saveToCookie();
		return state;
	}

	public getState(): {
		state: CircuitBreakerState;
		pass: number;
		fail: number;
		ratio: number;
		count: number;
	} {
		const onesAndZeroes = this.cookieData.numberRepresentation.toString(2);
		const pass = onesAndZeroes.match(/1/g)?.length ?? 0;
		const fail = this.cookieData.windowLength - pass;
		const ratio = this.cookieData.windowLength > 0 ? pass / this.cookieData.windowLength : 0;
		let state: CircuitBreakerState = 'CLOSED';
		if (this.cookieData.windowLength === this.windowSize && ratio < 1 - this.threshold) {
			if (
				this.cookieData.counter > this.keepOpenCount &&
				this.cookieData.counter <= this.keepOpenCount + this.halfOpenForCount
			) {
				state = 'HALF_OPEN';
			} else {
				state = 'OPEN';
			}
		}
		return {
			state,
			pass,
			fail,
			ratio,
			count: this.cookieData.counter,
		};
	}
}
